import { useCallback, useEffect, useRef, useState } from "react";
import { api, API, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Camera, Send, MessageSquare, Circle, ImagePlus, Wrench } from "lucide-react";
import { toast } from "sonner";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useAuth } from "@/contexts/AuthContext";

const EVENT_TONE = {
  resident_note: "border-indigo-500/40 text-indigo-300",
  vendor_note: "border-emerald-500/40 text-emerald-300",
  admin_note: "border-orange-500/40 text-orange-300",
  board_note: "border-violet-500/40 text-violet-300",
  photo: "border-amber-500/40 text-amber-300",
  status_change: "border-white/10 text-zinc-400",
  bid_placed: "border-emerald-500/40 text-emerald-300",
  vendor_assigned: "border-orange-500/40 text-orange-300",
  created: "border-white/10 text-zinc-400",
};

const humanEvent = (t) => (t || "event").replaceAll("_", " ");

/**
 * TicketTimelinePanel — reusable live timeline for a maintenance ticket.
 * Anyone with access can post a note; residents can drop photos.
 */
export function TicketTimelinePanel({ ticket, onClose }) {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const [events, setEvents] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/maintenance/${ticket.id}/timeline`);
      setEvents(data.items || []);
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) { if (typeof console !== 'undefined') console.debug('components/TicketTimelinePanel.jsx: swallowed', e); }
  }, [ticket.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const unsub = subscribe(`ticket:${ticket.id}`, ({ event, payload }) => {
      if (event === "timeline.new" && payload) {
        setEvents((prev) => (prev.some((e) => e.id === payload.id) ? prev : [...prev, payload]));
      }
    });
    return unsub;
  }, [subscribe, ticket.id]);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/maintenance/${ticket.id}/timeline`, { body: text.trim() });
      setText("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to post"); }
    setBusy(false);
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/maintenance/${ticket.id}/timeline/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo added");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Photo upload failed"); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Card className="p-4 border-white/10 bg-card" data-testid={`ticket-timeline-${ticket.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-orange-400" />
          <div>
            <div className="text-sm font-semibold text-zinc-100">{ticket.title || "Ticket timeline"}</div>
            <div className="text-[11px] text-zinc-500">Live updates · Everyone on the ticket sees these in real time</div>
          </div>
        </div>
        <Badge variant="outline" className="border-white/10 text-zinc-400" data-testid={`ticket-status-${ticket.id}`}>
          <Circle className="h-2 w-2 mr-1 fill-current" /> {ticket.status || "open"}
        </Badge>
      </div>

      <div ref={scrollRef}
           className="max-h-72 overflow-y-auto space-y-2 pr-1 mb-3"
           data-testid={`timeline-list-${ticket.id}`}>
        {events.length === 0 ? (
          <div className="text-xs text-zinc-500 italic py-6 text-center" data-testid={`timeline-empty-${ticket.id}`}>
            No events yet — post an update or drop a photo below.
          </div>
        ) : events.map((ev) => (
          <div key={ev.id} className="rounded-md border border-white/5 bg-white/[0.02] p-2.5"
               data-testid={`timeline-event-${ev.id}`}>
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <Badge variant="outline" className={EVENT_TONE[ev.event_type] || EVENT_TONE.created}>
                {humanEvent(ev.event_type)}
              </Badge>
              <span className="text-[11px] text-zinc-100 font-medium">{ev.actor_name || "System"}</span>
              <span className="text-[10px] text-zinc-500">· {ev.actor_role}</span>
              <span className="text-[10px] text-zinc-500 ml-auto tabular-nums">
                {new Date(ev.created_at).toLocaleString()}
              </span>
            </div>
            {ev.body && (
              <div className="text-xs text-zinc-300 whitespace-pre-wrap">{ev.body}</div>
            )}
            {(ev.photo_ids || []).length > 0 && (
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {ev.photo_ids.map((pid) => (
                  <a key={pid} href={`${API}/maintenance/timeline/photos/${pid}`}
                     target="_blank" rel="noreferrer"
                     data-testid={`timeline-photo-${pid}`}
                     className="block h-16 w-16 rounded-md overflow-hidden bg-black/40 border border-white/10 hover:border-orange-400">
                    <img src={`${API}/maintenance/timeline/photos/${pid}`}
                         alt="Ticket evidence"
                         className="h-full w-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t border-white/5 pt-3">
        <div className="flex items-end gap-2">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
                    placeholder={user?.role === "vendor" ? "Reply with an update…" : "Ask a question or add context…"}
                    className="bg-white/[0.04] border-white/10 text-xs"
                    data-testid={`timeline-input-${ticket.id}`} />
          <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/webp"
                 onChange={(e) => uploadPhoto(e.target.files?.[0])}
                 data-testid={`timeline-file-${ticket.id}`} />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
                  disabled={busy} className="border-white/10 rounded-full h-9"
                  data-testid={`timeline-photo-btn-${ticket.id}`}>
            <ImagePlus className="h-3 w-3 mr-1" /> Photo
          </Button>
          <Button size="sm" onClick={post} disabled={busy || !text.trim()}
                  className="bg-indigo-500 hover:bg-indigo-600 rounded-full h-9"
                  data-testid={`timeline-send-${ticket.id}`}>
            <Send className="h-3 w-3 mr-1" /> Send
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default TicketTimelinePanel;
